import {
    Unsubscribe,
    Metadata,
} from '@superbet-group/web.lib.feature-toggle-service';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import User from '@core/models/user/User';
import store from '@store/index';
import {
    Ref,
    UnwrapRef,
    ref,
    unref,
    computed,
    onMounted,
    onBeforeUnmount,
} from '@vue/composition-api';

/**
 * A callback to handle the flag value being ready for current user (or no user if logged off).
 *
 * @param value The flag value.
 * @param user The user (or null if no user is logged in).
 */
type OnFlagValueReadyCallback<T> = (value: UnwrapRef<T>, user: User | null) => void | Promise<void>;

/**
 * A hook to handle the flag value being ready.
 *
 * This should be called during the setup of the component. It will add a callback to be triggered when the flag is ready.
 *
 * The callback is triggered the first time the flag is read from Launch Darkly, and every time the flag is updated.
 *
 * If Launch Darkly is unreachable, the initial value will be used.
 *
 * @param callback The callback to be triggered when the flag is ready.
 */
type OnFlagValueReadyHook<T> = (callback: OnFlagValueReadyCallback<T>) => void;

/**
 * An array with the ref to the flag value and a hook to handle the flag being ready.
 */
type GetFeatureFlagReturnValue<T> = [ Ref<UnwrapRef<T>>, OnFlagValueReadyHook<T> ];

/**
 * The timeout for retrying reading the flag.
 */
const fallbackTimeout = 3000;

/**
 * Initial value of the attempts count.
 */
const initialAttemptNumber = 0;

/**
 * The maximum number of attempts to read the flag.
 */
const maxAttempts = 3;

/**
 * Reads a feature flag, making sure that the Launch Darkly context is correctly set.
 *
 * This will use getFeatureValue, not subscribeToFeatureChanges, meaning it WILL NOT listen to flag changes in real time.
 *
 * It will, however, respond to context changes, meaning the flag will be updated when the user logs in or out.
 *
 * @param name The name of the flag.
 * @param defaultValue The initial value of the flag, and also the fallback value when the flag is not found or coult not be read.
 * @returns An array with the ref to the flag value and a hook to handle the flag being ready.
 */
export function useGetFeatureFlag<T extends string | boolean | undefined>(name: string, defaultValue: T): GetFeatureFlagReturnValue<T> {
    // sometimes the Launch Darkly context is not set up, but the user is.
    // when it is not the case we can just read the flag value and get on with it.
    // however, when it is the case, we would get the wrong flag value if we tried to read it.
    // we would either:
    // - get the flag for an anonymous user, when in truth there is a logged one.
    // - get the flag for some user, when in truth the user has just logged off.
    // so we:
    // 1. only perform the read if the context matches the logged user.
    // 2. perform requests again if the context changes.
    // 3. retry after some seconds if request fails.

    const flag = ref<T>(defaultValue);
    const user = computed(() => store.getters['data/authentication/user'] as User | null);
    const callbacks: OnFlagValueReadyCallback<T>[] = [];

    let unsubscribeContextChange: Unsubscribe | undefined;
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;

    let lastUser: User | null = null;
    let ready = false;

    onMounted(async () => {
        const isUserAnonymousInLD = !!(featureFlagService.lastContext.user?.anonymous ?? true);
        const isUserAnonymous = (user.value === null);
        if (isUserAnonymousInLD === isUserAnonymous) {
            await readAndUpdateFlag();
        }
        unsubscribeContextChange = featureFlagService.subscribeToContextChange(() => {
            // We don't need the new context value, as it is already used inside the LD service.
            // We just need to know that it has changed.
            readAndUpdateFlag();
        });
    });

    onBeforeUnmount(() => {
        clearTimer();
        unsubscribeContextChange?.();
    });

    function clearTimer() {
        if (retryTimeout !== undefined) {
            clearTimeout(retryTimeout);
            retryTimeout = undefined;
        }
    }

    async function readAndUpdateFlag(attemptNumber: number = initialAttemptNumber) {
        clearTimer();

        let value: T;
        if (attemptNumber < maxAttempts) {
            let metadata: Metadata;
            try {
                const result = await featureFlagService.getFeatureValue(name, defaultValue, {
                    includeMetadata: true,
                });
                value = result.value;
                metadata = result.metadata;
            } catch (error) {
                // There was an error while reading the flag.
                // Try again after a timeout.
                clearTimer();
                retryTimeout = setTimeout(() => {
                    readAndUpdateFlag(attemptNumber + 1);
                }, fallbackTimeout);
                return;
            }

            const wasUserAnonymousInLD = !!(metadata.context.user?.anonymous ?? true);
            const isUserAnonymous = (user.value === null);
            if (wasUserAnonymousInLD !== isUserAnonymous) {
                // LaunchDarkly had the incorrect user at the time of the request, so the data it got is not correct.
                const isUserAnonymousInLD = !!(featureFlagService.lastContext.user?.anonymous ?? true);
                if (isUserAnonymousInLD === isUserAnonymous) {
                    // LaunchDarkly already has the correct user, it's just a matter of us reading the flag again.
                    // no need do await, we don't want it to be blocking anyway.
                    readAndUpdateFlag(attemptNumber + 1);
                    return;
                } else {
                    // LaunchDarkly still has the incorrect user, we will wait for it to be updated.
                    return;
                }
            }
        } else {
            value = defaultValue;
        }

        const newValue = unref(value) as UnwrapRef<T>;

        // since LD returns plain values (either undefined, string, or boolean), we can safely use simple comparison (!==) here.
        if (!ready || flag.value !== newValue || lastUser !== user.value) {
            flag.value = newValue;
            lastUser = user.value;
            ready = true;
            await Promise.all(callbacks.map((callback) => callback(newValue, lastUser)));
        }
    }

    function addCallback(callback: (value: UnwrapRef<T>, user: User | null) => void | Promise<void>) {
        callbacks.push(callback);
    }

    return [flag, addCallback];
}
